import type { components } from '../api/portal-api/portal-api-definition';
import { NloError } from '#imports';

/**
 * Parses the given input error to something we understand in the rest of the application.
 * @param input
 */
export function getNloError(input: NloError | any): NloError {
	try {
		// H3Error instances are thrown by Nuxt/H3, and may include a cause which is coming from our own code.
		const err = input.cause instanceof NloError ? input.cause : input;

		if (err instanceof NloApiError) {
			// Error from the fetcher api calls.
			const problemDetails = err.data as
				| components['schemas']['ValidationProblemDetails']
				| components['schemas']['ProblemDetails']
				| components['schemas']['ErrorResultProblemDetails']
				| undefined;
			if (problemDetails?._problemDetailsType === 'ValidationProblemDetails') {
				return new NloApiValidationError(err);
			} else if (problemDetails?._problemDetailsType === 'ErrorResultProblemDetails') {
				return new NloPortalApiError(err);
			} else if (err.status === 401) {
				return new NloNotAuthenticatedError(err);
			} else {
				return err;
			}
		} else if (input instanceof NloError) {
			return input;
		} else if (err instanceof NloError) {
			return err;
		} else if (err.data && err.data._problemDetailsType === 'ErrorResultProblemDetails') {
			// Error types coming from the Portal C# api will have problem details specifically typed for common api endpoints from portal.
			return new NloPortalApiError(err);
		} else {
			return getErrorFromUnknown(err);
		}
	} catch (e) {
		// This handler should never fail
		return new NloFailedToParseError(e);
	}
}

/**
 * Maps errors that weren't identified by regular instanceof checks.
 * @param input
 */
function getErrorFromUnknown(input: any): NloError {
	if (input.name === 'AbortError') {
		return new NloAbortError(input);
	}

	if (input.message.includes('Failed to fetch dynamically imported module:')) {
		return new NloClientOutdatedError();
	}

	switch (input.message) {
		case 'Failed to fetch': {
			return new NloNetworkError(input);
		}
		// String matches based on consts used in other types in case Nuxt/Nitro wraps our error in something else.
		// If someone has a better solution, please apply it.
		case RECAPTCHA_MISSING_ERROR_MESSAGE: {
			return new NloMissingRecaptchaError(input);
		}
		case NOT_AUTHENTICATED_ERROR_MESSAGE: {
			return new NloNotAuthenticatedError(input);
		}
		case NETWORK_ERROR_MESSAGE: {
			return new NloNetworkError(input);
		}
		case ABORT_ERROR_MESSAGE: {
			return new NloAbortError(input);
		}
		default: {
			return new NloUnknownError({ message: input.message, cause: input });
		}
	}
}
